// compSortingDataStore.ts

import {
  type CompSortCriterion,
  type CompSortOrder,
  sortOptions,
} from "@/types/compTypes"
import { map, computed } from "nanostores"

// Convert sortOptions to the format suitable for the store's initial state
const defaultOrders = Object.keys(sortOptions).reduce<
  Record<CompSortCriterion, CompSortOrder>
>(
  (acc, key) => {
    const { criterion, order } = sortOptions[key]
    acc[criterion] = order
    return acc
  },
  // eslint-disable-next-line @typescript-eslint/prefer-reduce-type-parameter, @typescript-eslint/consistent-type-assertions
  {} as Record<CompSortCriterion, CompSortOrder>
)

// Define the structure for the sorting state and initialize the sorting state map
interface SortingState {
  currentCriterion: CompSortCriterion
  orders: Record<CompSortCriterion, CompSortOrder>
}

export const currentSortingState = map<SortingState>({
  currentCriterion: "suggested",
  orders: defaultOrders,
})

// Computed store to check if the current sorting order is reversed
export const isCurrentSortReversed = computed(
  currentSortingState,
  (state) => state.orders[state.currentCriterion] === "reverse"
)

// Function to update the current sorting criterion (and optionally its order)
export function updateSorting(
  criterion: CompSortCriterion,
  order?: CompSortOrder
) {
  const currentState = currentSortingState.get()
  const safeCriterion = criterion || "suggested" // Fallback to 'suggested' if criterion is undefined
  const isSameCriterionSelected =
    safeCriterion === currentState.currentCriterion

  if (safeCriterion === "suggested" && isSameCriterionSelected) {
    // reversing suggested doesn't make sense.
    return
  }

  if (isSameCriterionSelected && !order) {
    // If the same criterion is selected again and no order is explicitly provided, toggle the order
    const currentOrder = currentState.orders[safeCriterion]
    const newOrder = currentOrder === "default" ? "reverse" : "default"
    currentSortingState.setKey("orders", {
      ...currentState.orders,
      [safeCriterion]: newOrder,
    })
  } else {
    // If it's a new criterion or an order is explicitly provided, update the state accordingly
    currentSortingState.setKey("currentCriterion", safeCriterion)
    if (order) {
      currentSortingState.setKey("orders", {
        ...currentState.orders,
        [safeCriterion]: order,
      })
    } else if (!isSameCriterionSelected) {
      // If it's a new criterion and no order is provided, reset to the default order from initial state
      // This step ensures a new criterion starts with its default order
      const defaultOrder = defaultOrders[safeCriterion] || "default"
      currentSortingState.setKey("orders", {
        ...currentState.orders,
        [safeCriterion]: defaultOrder,
      })
    }
  }
}

// Function to toggle the sort order for the specified sorting criterion
export function toggleSortOrderForCriterion(criterion: CompSortCriterion) {
  const currentOrders = currentSortingState.get().orders
  const currentOrder = currentOrders[criterion]
  const newOrder = currentOrder === "default" ? "reverse" : "default"
  currentSortingState.setKey("orders", {
    ...currentOrders,
    [criterion]: newOrder,
  })
}

// Optionally, a function to toggle the sort order for the current sorting criterion
export function toggleCurrentSortOrder() {
  const currentState = currentSortingState.get()
  const currentOrder = currentState.orders[currentState.currentCriterion]
  const newOrder = currentOrder === "default" ? "reverse" : "default"
  currentSortingState.setKey("orders", {
    ...currentState.orders,
    [currentState.currentCriterion]: newOrder,
  })
}

export const currentCriterionStore = computed(
  currentSortingState,
  (state) => state.currentCriterion
)
